import { useForm, Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import { Star, X, Send, CheckCircle, Smartphone, Monitor, AlertCircle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { updateUserFeedbackStatusServices } from "../../../../services/users.services";


const FeedbackFormV1 = ({ onClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);


  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      overallRating: 0,
      // firstImpression: "",
      // easeOfUse: "",
      // newFeatures: "",
      // issueDescription: "",
      // likedMost: "",
      improvement: "",
      device: "",
    },
  });


  const overallRating = watch("overallRating");
  const newFeatures = watch("newFeatures");


  // Confetti effect function
  const createConfetti = () => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };


    const randomInRange = (min, max) => Math.random() * (max - min) + min;


    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();


      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }


      const particleCount = 50 * (timeLeft / duration);


      // Create confetti from different positions
      createConfettiParticles(
        Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        })
      );
      createConfettiParticles(
        Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        })
      );
    }, 250);
  };


  // Create individual confetti particles using canvas
  const createConfettiParticles = (options) => {
    const canvas = document.getElementById("confetti-canvas");
    if (!canvas) return;


    const ctx = canvas.getContext("2d");
    const particles = [];
    const colors = ["#60a5fa", "#818cf8", "#a78bfa", "#f472b6", "#fb923c", "#fbbf24"];


    for (let i = 0; i < options.particleCount; i++) {
      particles.push({
        x: canvas.width * options.origin.x,
        y: canvas.height * options.origin.y,
        vx: (Math.random() - 0.5) * (options.spread / 10),
        vy: -Math.random() * options.startVelocity - 5,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        size: Math.random() * 8 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: options.ticks,
      });
    }


    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);


      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.5; // Gravity
        particle.rotation += particle.rotationSpeed;
        particle.life--;


        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.rotation * Math.PI) / 180);
        ctx.fillStyle = particle.color;
        ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        ctx.restore();


        if (particle.life <= 0 || particle.y > canvas.height) {
          particles.splice(index, 1);
        }
      });


      if (particles.length > 0) {
        requestAnimationFrame(animate);
      }
    };


    animate();
  };


  const { mutate: updateUserFeedBackCount } = useMutation({
    mutationFn: ({ userId }) =>
      updateUserFeedbackStatusServices({ userId }),
    onSuccess: (data) => {
      localStorage.setItem("dashboardFeedbackStatus", 1);
    },
    onError: (error) => {
      console.error("Error updating acknowledgment status:", error);
    }
  });

  // Handle form submission
  // Handle form submission
  const onSubmit = async (data) => {
    console.log("Feedback submitted:", data);

    // Create FormData object
    const formData = new FormData();

    // Append timestamp
    formData.append("timestamp", new Date().toISOString());

    // Append user information from localStorage
    formData.append("name", (localStorage.getItem("username") || "") + " " + (localStorage.getItem("lastname") || ""));
    formData.append("email", localStorage.getItem("email") || "");
    formData.append("enrollmentId", localStorage.getItem("enrol_id") || "");

    // Append form data
    formData.append("overallRating", data.overallRating);
    // formData.append("firstImpression", data.firstImpression);
    // formData.append("easeOfUse", data.easeOfUse);
    // formData.append("newFeaturesInteraction", data.newFeatures);
    // formData.append("issueDescription", data.issueDescription || "N/A");
    // formData.append("likedMost", data.likedMost);
    formData.append("improvementSuggestions", data.improvement || "N/A");
    formData.append("deviceType", data.device);

    try {
      // Send FormData to your API
      const response = await fetch("https://script.google.com/macros/s/AKfycbziYHhuvuIOCubtlG8mG4Z2QilGD6EGMdQ0NUA3WbHgvJgSOkfA1qP1RERhokzpPtI/exec", {
        method: "POST",
        body: formData,
      });

      updateUserFeedBackCount({ userId: Number(localStorage.getItem("user_id")) });
      if (response.ok) {
        console.log("Feedback submitted successfully!");

        setIsSubmitted(true);
        setShowConfetti(true);


        // Trigger confetti
        setTimeout(() => {
          createConfetti();
        }, 100);


        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        console.error("Failed to submit feedback");
        // Handle error - you can show an error message to user
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Handle error - you can show an error message to user
    }
  };



  // Handle star click
  const handleStarClick = (value) => {
    setValue("overallRating", value, { shouldValidate: true });
  };


  // Update canvas size on window resize
  useEffect(() => {
    const canvas = document.getElementById("confetti-canvas");
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }


    const handleResize = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };


    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSubmitted]);


  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn p-4">
      {/* Confetti Canvas */}
      {showConfetti && (
        <canvas
          id="confetti-canvas"
          className="fixed inset-0 pointer-events-none"
          style={{ zIndex: 10000 }}
        />
      )}


      <div
        className="bg-white rounded-2xl shadow-2xl max-w-[39rem] w-full max-h-[90vh] overflow-hidden animate-slideUp relative"
        style={{
          animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {!isSubmitted ? (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="absolute top-8 right-4 text-white  font-semibold hover:text-white text-sm px-3 py-2 bg-white/30 rounded transition-all duration-200"
                aria-label="Close feedback modal"
              >
                Skip
              </button>



              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                  <Send className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">We'd love your quick feedback</h2>
                  <p className="text-blue-100 text-sm mt-1">It takes less than 5 seconds!</p>
                </div>
              </div>
            </div>


            {/* Form Content */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]"
            >
              {/* Question 1: Overall Rating */}
              <div className="space-y-2 pb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  1. How would you rate your overall experience with the new dashboard?
                  <span className="text-red-500 ml-1">*</span>
                </label>


                <div className="flex items-center justify-center gap-2 py-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleStarClick(rating)}
                      onMouseEnter={() => setHoveredRating(rating)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="transition-all duration-200 transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full p-1"
                    >
                      <Star
                        className={`w-10 h-10 transition-all duration-200 ${rating <= (hoveredRating || overallRating)
                          ? "fill-yellow-400 text-yellow-400 scale-110"
                          : "text-gray-300"
                          }`}
                        style={{
                          filter:
                            rating <= (hoveredRating || overallRating)
                              ? "drop-shadow(0 0 8px rgba(250, 204, 21, 0.5))"
                              : "none",
                        }}
                      />
                    </button>
                  ))}
                </div>


                <div className="flex justify-between text-xs text-gray-500 px-2">
                  <span>1 – Very poor</span>
                  <span>5 – Excellent</span>
                </div>


                <input
                  type="hidden"
                  {...register("overallRating", {
                    required: "Please select a rating",
                    min: { value: 1, message: "Please select at least 1 star" },
                  })}
                />
                {errors.overallRating && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.overallRating.message}</span>
                  </div>
                )}
              </div>


              {/* Question 2: First Impression */}
              {/* <div className="space-y-3 pb-4 border-b border-gray-200">
                <label className="block text-sm font-semibold text-gray-700">
                  2. What's your first impression of the new design and layout?
                  <span className="text-red-500 ml-1">*</span>
                </label>


                <div className="space-y-2">
                  {[
                    { value: "love-it", label: "Love it - clean and easy to use" },
                    { value: "good", label: "It's good, but can be improved" },
                    { value: "neutral", label: "Neutral" },
                    { value: "confusing", label: "It feels confusing" },
                    { value: "prefer-old", label: "I prefer the old version" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                    >
                      <input
                        type="radio"
                        {...register("firstImpression", {
                          required: "Please select an option",
                        })}
                        value={option.value}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>


                {errors.firstImpression && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.firstImpression.message}</span>
                  </div>
                )}
              </div> */}


              {/* Question 3: Ease of Use */}
              {/* <div className="space-y-3 pb-4 border-b border-gray-200">
                <label className="block text-sm font-semibold text-gray-700">
                  3. How easy was it to find what you were looking for (e.g., courses, attendance, or
                  calendar)?
                  <span className="text-red-500 ml-1">*</span>
                </label>


                <div className="space-y-2">
                  {[
                    { value: "very-easy", label: "Very easy" },
                    { value: "somewhat-easy", label: "Somewhat easy" },
                    { value: "neutral", label: "Neutral" },
                    { value: "somewhat-difficult", label: "Somewhat difficult" },
                    { value: "very-difficult", label: "Very difficult" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                    >
                      <input
                        type="radio"
                        {...register("easeOfUse", {
                          required: "Please select an option",
                        })}
                        value={option.value}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>


                {errors.easeOfUse && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.easeOfUse.message}</span>
                  </div>
                )}
              </div> */}


              {/* Question 4: New Features - WITH CONDITIONAL REQUIRED VALIDATION */}
              {/* <div className="space-y-3 pb-4 border-b border-gray-200">
                <label className="block text-sm font-semibold text-gray-700">
                  4. Did you interact with any new features (like Courses, Resource Library, Challenge of
                  the Day)?
                  <span className="text-red-500 ml-1">*</span>
                </label>


                <div className="space-y-2">
                  {[
                    { value: "yes-worked", label: "Yes, and they worked well" },
                    { value: "yes-issues", label: "Yes, but I faced some issues" },
                    { value: "no-not-yet", label: "No, not yet" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                    >
                      <input
                        type="radio"
                        {...register("newFeatures", {
                          required: "Please select an option",
                        })}
                        value={option.value}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>


                {errors.newFeatures && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.newFeatures.message}</span>
                  </div>
                )}


                {newFeatures === "yes-issues" && (
                  <div className="mt-3 animate-slideDown">
                    <label
                      htmlFor="issueDescription"
                      className="block text-sm font-medium text-gray-600 mb-2"
                    >
                      Please describe briefly what didn't work as expected:
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      id="issueDescription"
                      {...register("issueDescription", {
                        required: newFeatures === "yes-issues" ? "Please describe the issue you faced" : false,
                        minLength: {
                          value: 10,
                          message: "Please provide at least 10 characters",
                        },
                      })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Tell us what went wrong..."
                    />
                    {errors.issueDescription && (
                      <div className="flex items-center gap-2 text-red-500 text-sm rounded-lg p-1 mt-1 animate-shake">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{errors.issueDescription.message}</span>
                      </div>
                    )}
                  </div>
                )}
              </div> */}


              {/* Question 5: Liked Most */}
              {/* <div className="space-y-3 pb-4 border-b border-gray-200">
                <label htmlFor="likedMost" className="block text-sm font-semibold text-gray-700">
                  5. What's one thing you liked most about the new dashboard?
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="likedMost"
                  {...register("likedMost", {
                    required: "Please share what you liked",
                    minLength: {
                      value: 5,
                      message: "Please provide at least 5 characters",
                    },
                  })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Share what you enjoyed most..."
                />
                {errors.likedMost && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.likedMost.message}</span>
                  </div>
                )}
              </div> */}


              {/* Question 6: Improvement */}
              <div className="space-y-2 pb-2">
                <label htmlFor="improvement" className="block text-sm font-semibold  text-gray-700">
                  2. Did you face any issues or have suggestions for improvement?
                </label>
                <textarea
                  id="improvement"
                  {...register("improvement")}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-y min-h-20"
                  placeholder="Share your ideas (optional)..."
                />
              </div>




              {/* Question 7: Device */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  3. Did you access the dashboard on mobile or desktop?
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <div className="flex gap-3">
                  <label className="relative flex-1 flex flex-col items-center justify-center py-3 px-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                    <input
                      type="radio"
                      {...register("device", {
                        required: "Please select your device",
                      })}
                      value="mobile"
                      className="sr-only peer"
                    />
                    <Smartphone className="w-5 h-5 text-gray-400 peer-checked:text-blue-600 mb-1 transition-colors" />
                    <span className="text-xs font-medium text-gray-600 peer-checked:text-blue-600 transition-colors">
                      Mobile
                    </span>
                    <div className="absolute inset-0 border-2 border-blue-600 rounded-xl opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                  </label>

                  <label className="relative flex-1 flex flex-col items-center justify-center py-3 px-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                    <input
                      type="radio"
                      {...register("device", {
                        required: "Please select your device",
                      })}
                      value="desktop"
                      className="sr-only peer"
                    />
                    <Monitor className="w-5 h-5 text-gray-400 peer-checked:text-blue-600 mb-1 transition-colors" />
                    <span className="text-xs font-medium text-gray-600 peer-checked:text-blue-600 transition-colors">
                      Desktop
                    </span>
                    <div className="absolute inset-0 border-2 border-blue-600 rounded-xl opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                  </label>
                </div>

                {errors.device && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3 animate-shake">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.device.message}</span>
                  </div>
                )}
              </div>




              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 shadow-lg hover:shadow-xl mt-6"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          // Success State with Enhanced Animation
          // Success State with Theme Colors (Blue/Indigo)
          <div className="p-12 text-center animate-scaleIn relative overflow-hidden">
            {/* Animated Background Circles - Blue Theme */}
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <div className="w-64 h-64 bg-blue-500 rounded-full animate-ping"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-5">
              <div
                className="w-96 h-96 bg-indigo-400 rounded-full animate-pulse"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>


            {/* Success Icon with Animation - Blue Theme */}
            <div className="mb-6 flex justify-center relative z-10">
              <div className="relative">
                <div className="bg-blue-100 rounded-full p-4 animate-bounce">
                  <CheckCircle className="w-16 h-16 text-blue-600" />
                </div>
                {/* Ripple Effect - Blue Theme */}
                <div className="absolute inset-0 bg-blue-200 rounded-full animate-ping opacity-75"></div>
              </div>
            </div>


            {/* Success Text */}
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-3 animate-slideInUp">
                Thanks for sharing!
              </h3>
              <p className="text-gray-600 text-lg animate-slideInUp" style={{ animationDelay: "0.1s" }}>
                Your input helps us make your learning experience smoother.
              </p>


              {/* Success Checkmarks - Blue Theme */}
              <div className="mt-6 flex justify-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>


        )}
      </div>


      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }


        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }


        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }


        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          50% {
            transform: scale(1.05);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }


        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }


        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }


        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }


        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }


        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }


        .animate-scaleIn {
          animation: scaleIn 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }


        .animate-slideInUp {
          animation: slideInUp 0.5s ease-out forwards;
          opacity: 0;
        }


      `}</style>
    </div>
  );
};


export default FeedbackFormV1;
